import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@Components/common';
import styles from './DocumentsController.module.scss';
import { Documents } from '@Components/AccountPage/Documents/Documents';
import { OrganizationDocumentTypesEnum } from '@Types/OrganizationDueDiligenceData';
import { UploadItem } from '@Types';
import { useNotificationsContext, useUserContext } from '@Contexts';
import { useGetDueDiligenceDetails, useUpdateDueDiligenceDetails } from '@Hooks/Api';
import { Loader } from '@Components';
import { ConfirmModal } from '@Components/ConfirmModal/ConfirmModal';
import { cleanNumericFormatField } from '@Helpers/cleanNumericFormatField';
import { setBackendFieldErrors } from '@Helpers/setBackendFieldErrors';
import { useFormGuard } from '@Hooks/useFormGuard';

interface DocumentsControllerProps {
  afterSubmit?: () => void;
  onFormStateChange?: (hasUnsavedChanges: boolean) => void;
}

interface FormData {
  governing_documents: UploadItem[];
  annual_accounts: UploadItem[];
  annual_income?: string;
}

export const DocumentsController = ({
  afterSubmit,
  onFormStateChange,
}: DocumentsControllerProps) => {
  const [hasActiveUploads, setHasActiveUploads] = useState(false);
  const [originalData, setOriginalData] = useState<FormData | null>(null);
  const { t } = useTranslation('account');
  const methods = useForm<FormData>({
    defaultValues: {
      governing_documents: [],
      annual_accounts: [],
    },
  });
  const { reset, setError, handleSubmit, formState } = methods;
  const { success: successNotification, error: errorNotification } = useNotificationsContext();
  const { userData } = useUserContext();
  const [getDueDiligenceData, { loading: dueDiligenceDataLoading }] = useGetDueDiligenceDetails();
  const [updateDueDiligenceData, { loading: isUpdateDueDiligenceDataLoading }] =
    useUpdateDueDiligenceDetails();
  const organizationId = userData?.organization_id;
  const [governingDocsUploading, setGoverningDocsUploading] = useState(false);
  const [annualAccountsUploading, setAnnualAccountsUploading] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Load initial data.
  useEffect(() => {
    if (organizationId && !hasInitiallyLoaded) {
      getDueDiligenceData(organizationId).then(({ data }) => {
        const filteredData: FormData = {
          governing_documents: (data?.key_documents ?? [])
            .filter(
              (document) =>
                document.document_type === OrganizationDocumentTypesEnum.GOVERNING_DOCUMENT,
            )
            .map((doc) => ({
              id: doc.id,
              filename: doc.filename,
              url: doc.url,
            })),
          annual_accounts: (data?.key_documents ?? [])
            .filter(
              (document) =>
                document.document_type === OrganizationDocumentTypesEnum.ANNUAL_ACCOUNTS,
            )
            .map((doc) => ({
              id: doc.id,
              filename: doc.filename,
              url: doc.url,
            })),
          annual_income: data?.annual_accounts.income,
        };
        reset(filteredData);
        setOriginalData(filteredData);
        setHasInitiallyLoaded(true);
      });
    }
  }, [organizationId, hasInitiallyLoaded]);

  // Determine if we have active uploads
  useEffect(() => {
    const hasUploads = governingDocsUploading || annualAccountsUploading;
    setHasActiveUploads(hasUploads);
  }, [governingDocsUploading, annualAccountsUploading]);

  // Synchronous reset function for the form guard.
  const handleFormReset = useCallback(() => {
    if (originalData) {
      reset(originalData);
      // Clear upload states
      setGoverningDocsUploading(false);
      setAnnualAccountsUploading(false);
    }
  }, [originalData, reset]);

  const hasUnsavedChanges = useMemo(
    () => formState.isDirty || hasActiveUploads,
    [formState.isDirty, hasActiveUploads],
  );

  const { attemptReset, confirmationModal } = useFormGuard({
    isDirty: formState.isDirty,
    hasActiveUploads,
    onReset: handleFormReset,
  });

  // Notify parent about unsaved changes.
  useEffect(() => {
    onFormStateChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onFormStateChange]);

  const formatFormDataForSubmit = (data: FormData) => {
    const key_documents = [
      ...(data.annual_accounts || []).map((document) => ({
        id: document.id,
        document_type: OrganizationDocumentTypesEnum.ANNUAL_ACCOUNTS,
      })),
      ...(data.governing_documents || []).map((document) => ({
        id: document.id,
        document_type: OrganizationDocumentTypesEnum.GOVERNING_DOCUMENT,
      })),
    ];

    return {
      annual_accounts: {
        income: cleanNumericFormatField(data.annual_income),
      },
      key_documents,
    };
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!organizationId) return;

    try {
      const formattedData = formatFormDataForSubmit(data);
      await updateDueDiligenceData(organizationId, formattedData);

      successNotification(t('update_success_message', { section: t('tabs.documents.title') }));

      // Refetch data to get the URLs for newly uploaded documents.
      const { data: refreshedData } = await getDueDiligenceData(organizationId);
      const refreshedFilteredData: FormData = {
        governing_documents: (refreshedData?.key_documents ?? [])
          .filter(
            (document) =>
              document.document_type === OrganizationDocumentTypesEnum.GOVERNING_DOCUMENT,
          )
          .map((doc) => ({
            id: doc.id,
            filename: doc.filename,
            url: doc.url,
          })),
        annual_accounts: (refreshedData?.key_documents ?? [])
          .filter(
            (document) => document.document_type === OrganizationDocumentTypesEnum.ANNUAL_ACCOUNTS,
          )
          .map((doc) => ({
            id: doc.id,
            filename: doc.filename,
            url: doc.url,
          })),
        annual_income: refreshedData?.annual_accounts.income,
      };

      // Reset form state with refreshed data including URLs.
      setOriginalData(refreshedFilteredData);
      reset(refreshedFilteredData);

      afterSubmit?.();
    } catch (error: any) {
      if (error?.data?.field_errors) {
        const { field_errors } = error.data;
        if (field_errors.key_documents) {
          setBackendFieldErrors(field_errors.key_documents, setError);
        }
        if (field_errors.annual_accounts) {
          setBackendFieldErrors(field_errors.annual_accounts, setError);
        }
      } else {
        errorNotification(t('common:errors.generic_error'));
      }
    }
  });

  if (dueDiligenceDataLoading) {
    return <Loader />;
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className={styles.formContainer}>
          <Documents
            onGoverningDocsUploadChange={setGoverningDocsUploading}
            onAnnualAccountsUploadChange={setAnnualAccountsUploading}
          />
          <div className={styles.buttonContainer}>
            <Button
              buttonType="tertiary"
              className={styles.cancelButton}
              type="button"
              onClick={attemptReset}
              disabled={!hasUnsavedChanges || hasActiveUploads}
            >
              {t('tabs.documents.cancel_changes')}
            </Button>
            <Button
              buttonType="primary"
              className={styles.button}
              type="submit"
              loading={isUpdateDueDiligenceDataLoading}
              disabled={isUpdateDueDiligenceDataLoading || hasActiveUploads}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </FormProvider>

      <ConfirmModal
        isOpen={confirmationModal.isOpen}
        onClose={confirmationModal.onCancel}
        title={confirmationModal.title}
        cancelText={confirmationModal.cancelText}
        onCancel={confirmationModal.onCancel}
        confirmText={confirmationModal.confirmText}
        onConfirm={confirmationModal.onConfirm}
        isConfirmBtnDanger
        compact
      >
        {confirmationModal.message}
      </ConfirmModal>
    </>
  );
};
